// iter151 — "What's New" bottom sheet. Shows at most 3 short cards in
// English + Odia explaining the latest iteration's visible changes.
// Rules (user spec):
//   • Fires at most ONCE per app version (localStorage per-version key).
//   • Skipped for brand-new users on their first login — we only want
//     returning users to notice the change. "Brand new" = we don't yet
//     have a `firstSeenAt` stamp in localStorage, so we stamp now and
//     bail. The next deploy's version bump will show them the sheet.
//   • Never pops on /login, /register or any public route (gated by
//     the useAuth() check — this component bails if no user).
//   • Dismissed via "Got it" (marks seen) OR "Skip" (also marks seen).
//   • A permanent entry "What's New" under Help in the admin / school
//     MoreSheet re-opens it on demand regardless of the seen stamp.
import { useEffect, useMemo, useState } from "react";
import { Sparkles, Grid3x3, WifiOff, Download } from "lucide-react";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription,
} from "@/components/ui/drawer";
import { useAuth } from "@/lib/AuthContext";

// Bump this string each time there's a new "What's New" payload the
// user should see on login. Must match the keys of CARDS below.
export const WHATS_NEW_VERSION = "iter151-v1";
const SEEN_KEY = `mdm.whatsNewSeen.${WHATS_NEW_VERSION}`;
const FIRST_SEEN_KEY = "mdm.firstSeenAt";

const CARDS = [
  {
    Icon: Grid3x3,
    tint: "bg-indigo-100 text-indigo-700",
    titleEn: "New bottom menu + \"More\"",
    titleOr: "ନୂଆ ତଳ ମେନୁ ଓ \"More\" ବଟନ୍",
    bodyEn:
      "The bottom bar now shows 5 big tiles. Tap \"More\" to find Setup, Fund Management, Nudges and anything that doesn't fit on the bar.",
    bodyOr:
      "ତଳ ବାର୍ ଏବେ 5ଟି ବଡ଼ ଟାଇଲ୍ ଦେଖାଉଛି। Setup, Fund Management, Nudges ଆଦି ପାଇଁ \"More\" କ୍ଲିକ୍ କରନ୍ତୁ।",
  },
  {
    Icon: WifiOff,
    tint: "bg-emerald-100 text-emerald-700",
    titleEn: "Entries save without internet",
    titleOr: "ଇଣ୍ଟରନେଟ୍ ବିନା ବି Daily Entry ସେଭ୍ ହେବ",
    bodyEn:
      "Lost signal? Save your Daily Entry anyway. It stays on your phone and uploads itself the moment you're back online.",
    bodyOr:
      "ସିଗ୍ନାଲ୍ ନାହିଁ? ଚିନ୍ତା ନକରନ୍ତୁ। Entry ଫୋନରେ ସୁରକ୍ଷିତ ରହିବ ଓ ଇଣ୍ଟରନେଟ୍ ଆସିଲେ ଅଟୋମାଟିକ୍ ଅପଲୋଡ୍ ହୋଇଯିବ।",
  },
  {
    Icon: Download,
    tint: "bg-amber-100 text-amber-700",
    titleEn: "Install the app — Play Store soon",
    titleOr: "ଆପ୍ ଇନଷ୍ଟଲ୍ କରନ୍ତୁ — ଶୀଘ୍ର Play Store ରେ",
    bodyEn:
      "Add MDM Sahayak to your home screen now for a one-tap, fullscreen app. The Google Play version is on the way.",
    bodyOr:
      "ଏବେ MDM Sahayak କୁ Home Screen ରେ ଯୋଡ଼ନ୍ତୁ — ଗୋଟିଏ କ୍ଲିକରେ ଖୋଲିବ। Google Play ଭର୍ସନ୍ ଶୀଘ୍ର ଆସୁଛି।",
  },
];

function hasSeen() {
  try { return !!localStorage.getItem(SEEN_KEY); } catch { return true; }
}
function markSeen() {
  try { localStorage.setItem(SEEN_KEY, String(Date.now())); } catch { /* no-op */ }
}

// iter151 fix v3 — Module-level decision cache. Latched OUTSIDE React
// so React 18 StrictMode's dev double-invoke-with-cleanup cycle can't
// flip our brandNew judgement halfway. First invocation per page load
// decides and stamps; every subsequent invocation reads the cached
// decision.
let _wnDecision = null; // null | "brandnew" | "show"
function decideAndStamp() {
  if (_wnDecision !== null) return _wnDecision;
  try {
    const stamped = !!localStorage.getItem(FIRST_SEEN_KEY);
    if (!stamped) {
      localStorage.setItem(FIRST_SEEN_KEY, String(Date.now()));
      _wnDecision = "brandnew";
    } else {
      _wnDecision = "show";
    }
  } catch { _wnDecision = "show"; }
  return _wnDecision;
}

/** Standalone trigger — mounted inside AuthedShell. Fires only once
 * per app version per user per device. Returning users see it;
 * brand-new users skip the first cycle. */
export default function WhatsNewSheet({ forceOpen = false, onDone }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (forceOpen) { setIdx(0); setOpen(true); return undefined; }
    if (!user) return undefined;
    if (hasSeen()) return undefined;
    // iter161 — never pop on a Toolkit auto-login arrival; shows on the next normal visit.
    try { if (sessionStorage.getItem("mdm.ssoArrival")) return undefined; } catch { /* ignore */ }
    // Module-level decision — StrictMode-safe. See decideAndStamp above.
    if (decideAndStamp() === "brandnew") return undefined;
    // Delay slightly so other login-time popups (Odia / AdminMessage)
    // finish their entry animation first. We DO NOT clear this timer
    // on cleanup — dev-only StrictMode cleanup would otherwise cancel
    // the only scheduled open. In prod this effect runs once.
    setTimeout(() => { setIdx(0); setOpen(true); }, 1400);
    return undefined;
  }, [user, forceOpen]);

  const card = useMemo(() => CARDS[idx] || null, [idx]);
  const last = idx === CARDS.length - 1;

  const close = () => {
    setOpen(false);
    markSeen();
    onDone?.();
  };
  const next = () => {
    if (last) close();
    else setIdx((i) => i + 1);
  };

  if (!card) return null;
  const { Icon } = card;

  return (
    <Drawer
      open={open}
      onOpenChange={(o) => { if (!o) close(); }}
    >
      <DrawerContent
        data-testid="whats-new-sheet"
        className="max-h-[86vh] rounded-t-3xl border-t border-slate-200 bg-white"
      >
        <DrawerHeader className="text-left">
          <DrawerTitle className="flex items-center gap-2 text-base font-semibold text-slate-900">
            <Sparkles className="h-4 w-4 text-indigo-600" />
            What's New · ନୂଆ କଣ ଅଛି
          </DrawerTitle>
          <DrawerDescription className="text-[11px] text-slate-500">
            Card {idx + 1} of {CARDS.length}
          </DrawerDescription>
        </DrawerHeader>
        <div className="px-5 pb-4" data-testid={`whats-new-card-${idx}`}>
          <div className={`grid h-14 w-14 place-items-center rounded-2xl ${card.tint}`}>
            <Icon className="h-7 w-7" strokeWidth={2.25} />
          </div>
          <h3 className="mt-4 text-lg font-bold leading-tight text-slate-900">{card.titleEn}</h3>
          <p className="mt-1 text-sm font-semibold leading-tight text-slate-700">{card.titleOr}</p>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">{card.bodyEn}</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">{card.bodyOr}</p>
        </div>
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 pb-1">
          {CARDS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 w-6 rounded-full ${i === idx ? "bg-indigo-600" : "bg-slate-200"}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-3 border-t border-slate-100 bg-slate-50 px-5 py-3">
          <button
            type="button"
            onClick={close}
            data-testid="whats-new-skip"
            className="flex-1 rounded-xl bg-white px-4 py-3 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 active:bg-slate-100"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={next}
            data-testid="whats-new-next"
            className="flex-1 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white shadow active:scale-95"
          >
            {last ? "Got it" : "Next"}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

/** Imperative re-open — used by the Help → What's New menu item. */
export function openWhatsNew() {
  try { localStorage.removeItem(SEEN_KEY); } catch { /* no-op */ }
  // Dispatch a custom event; the AuthedShell listens for it and
  // flips its `forceOpen` state. Avoids wiring a global store just
  // for this single modal.
  window.dispatchEvent(new CustomEvent("mdm:open-whats-new"));
}
